import React, { useState, useEffect, useRef, useCallback, Component } from 'react';
import { UserProvider, useUser } from './context/UserContext';
import { ThemeProvider } from './context/ThemeContext';
import { SidePanelProvider } from './context/SidePanelContext';
import { PriceProvider, usePrices } from './context/PriceContext';
import { TodayProvider } from './context/TodayContext';
import TopBar        from './components/layout/TopBar';
import Sidebar       from './components/layout/Sidebar';
import SidePanel     from './components/layout/SidePanel';
import LoginPage     from './pages/LoginPage';
import PendingPage   from './pages/PendingPage';
import LockedTab     from './components/ui/LockedTab';
import DashboardTab  from './components/tabs/DashboardTab';
import NiftyTab      from './components/tabs/NiftyTab';
import PerformanceTab from './components/tabs/PerformanceTab';
import OptionsTab    from './components/tabs/OptionsTab';
import ScannerTab    from './components/tabs/ScannerTab';
import JournalTab    from './components/tabs/JournalTab';
import AISummaryTab  from './components/tabs/AISummaryTab';
import SettingsTab   from './components/tabs/SettingsTab';
import DeskTab       from './components/tabs/DeskTab';

// ── Per-tab error boundary — stops one crashing tab from killing the whole app ─
class TabErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(err) { return { error: err }; }
  componentDidCatch(err, info) { console.error('[TabErrorBoundary]', err, info?.componentStack); }
  render() {
    if (this.state.error) {
      return (
        <div style={{padding:24,color:'var(--down-field-ink)',fontSize:12,fontFamily:'monospace',background:'var(--surface-2)',borderRadius:6,margin:12}}>
          <strong>Tab error — check console for details</strong><br/><br/>
          {String(this.state.error)}
        </div>
      );
    }
    return this.props.children;
  }
}

// All valid tab IDs
const VALID_TABS = ['dashboard','nifty','performance','options','scanner','journal','ai','desk','settings'];

// Maps top-level tab ID → which permission key(s) gate access.
// null = handled specially (admin/isAdmin check).
// 'all' = any authenticated user (no specific permission needed).
// Market sub-tab keys — tab is accessible if user can see at least one sub-tab
const MARKET_SUB_KEYS = [
  'market_perf', 'market_rotation', 'market_rs',
  'market_drivers', 'market_events', 'market_directory',
];

const TAB_PERMISSIONS = {
  dashboard:   ['dashboard'],
  nifty:       ['nifty'],
  performance: MARKET_SUB_KEYS,   // Market tab shows if ≥1 sub-tab is accessible
  options:     ['options.option_chain', 'options.oi_analysis', 'options.strategy_builder'],
  scanner:     ['scanner'],
  journal:     ['journal.trade_log', 'journal.ai_analysis'],
  ai:          ['ai_summary'],
  desk:        'all',    // all authenticated users — observation monitoring
  settings:    'all',    // every authenticated user
};

// Background-mount tiers (ms after app load).
const PREFETCH_TIERS = [
  { delay:  500, tabs: ['dashboard', 'nifty'] },
  { delay: 1500, tabs: ['performance'] },
  { delay: 3500, tabs: ['scanner', 'options', 'journal', 'ai'] },
  // admin, users, settings mounted on demand only
];

function parseHash(hash) {
  const clean = (hash || '').replace(/^#/, '');
  const [tab, sub] = clean.split('/');
  const validTab = VALID_TABS.includes(tab) ? tab : 'dashboard';
  return { tab: validTab, sub: sub || '' };
}

function pushHash(tab, sub, replace = false) {
  const hash = sub ? `#${tab}/${sub}` : `#${tab}`;
  if (replace || window.location.hash === hash) {
    window.history.replaceState(null, '', hash);
  } else {
    window.history.pushState(null, '', hash);
  }
}

function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--paper)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--ink-3)',
      fontSize: 13,
      fontFamily: 'var(--font-ui)',
    }}>
      <span style={{ opacity: 0.5 }}>Loading…</span>
    </div>
  );
}

// ── Pull-to-refresh: in-place data update, no page reload ─────────────────────
// Replaces the browser's native pull-to-refresh (disabled via overscroll-behavior-y:none).
// Calls refresh() from PriceContext — old data stays visible while new data loads.
function usePullToRefresh(onRefresh) {
  const [state, setState] = useState('idle'); // idle | pulling | refreshing
  const [pullPx, setPullPx] = useState(0);
  const startY    = useRef(0);
  const THRESHOLD = 64; // px of intentional drag before release triggers refresh

  useEffect(() => {
    const onStart = e => {
      // Only arm when at top of page and body scroll isn't locked (panel not open)
      if (window.scrollY === 0 && document.body.style.overflow !== 'hidden') {
        startY.current = e.touches[0].clientY;
      } else {
        startY.current = -1;
      }
    };

    const onMove = e => {
      if (startY.current < 0) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && dy < 100 && window.scrollY === 0) {
        setState('pulling');
        setPullPx(Math.min(dy, THRESHOLD));
      } else if (dy <= 0) {
        setState('idle');
        setPullPx(0);
      }
    };

    const onEnd = async () => {
      if (state !== 'pulling') return;
      if (pullPx >= THRESHOLD) {
        setState('refreshing');
        setPullPx(0);
        try { await onRefresh(); } finally { setState('idle'); }
      } else {
        setState('idle');
        setPullPx(0);
      }
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove',  onMove,  { passive: true });
    document.addEventListener('touchend',   onEnd);
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove',  onMove);
      document.removeEventListener('touchend',   onEnd);
    };
  }, [state, pullPx, onRefresh]);

  return { state, pullPx };
}

function AppInner() {
  const { isAuthenticated, role, can, trackTabVisit } = useUser();
  const { refresh } = usePrices();

  // Pull-to-refresh — calls refresh() in-place, old data stays visible
  const handlePullRefresh = useCallback(async () => {
    try { await refresh(); } catch {}
  }, [refresh]);
  const { state: pullState, pullPx } = usePullToRefresh(handlePullRefresh);

  // Does user have access to a top-level tab?
  const canAccessTab = useCallback((tabId) => {
    const keys = TAB_PERMISSIONS[tabId];
    if (!keys) return false;
    if (keys === 'all') return true;                      // open to all authenticated users
    return keys.some(k => can(k));
  }, [can, role]);

  const [loc, setLoc] = useState(() => parseHash(window.location.hash));
  const [mountedTabs, setMountedTabs] = useState(
    () => new Set([parseHash(window.location.hash).tab])
  );
  const initSubRef = useRef(parseHash(window.location.hash).sub);

  // ── Sidebar state ─────────────────────────────────────────────────────
  const [sidebarCollapsed,   setSidebarCollapsed]   = useState(false);
  const [sidebarMobileOpen,  setSidebarMobileOpen]  = useState(false);

  // ── Background prefetch: accessible tabs only ─────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    const timers = PREFETCH_TIERS.map(({ delay, tabs }) =>
      setTimeout(() => {
        setMountedTabs(prev => {
          const next = new Set(prev);
          tabs.forEach(t => {
            if (!canAccessTab(t)) return;
            next.add(t);
          });
          return next;
        });
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [isAuthenticated, canAccessTab]);

  // ── Canvas redraw on tab switch ───────────────────────────────────────────
  const prevTabRef = useRef(loc.tab);
  useEffect(() => {
    if (loc.tab !== prevTabRef.current) {
      prevTabRef.current = loc.tab;
      const t = setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
      return () => clearTimeout(t);
    }
  }, [loc.tab]);

  useEffect(() => {
    const onPop = () => setLoc(parseHash(window.location.hash));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (!window.location.hash) {
      window.history.replaceState(null, '', '#dashboard');
    }
  }, []);

  // ── Auto-reload after 30 min in background ───────────────────────────────
  // When the PWA is resumed from the background after a long absence the JS in
  // memory is stale. Recording when we go hidden and reloading on return means
  // the user always gets fresh code — same effect as close + reopen, hands-free.
  const hiddenAtRef = useRef(null);
  useEffect(() => {
    const THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes
    const onVisChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        if (hiddenAtRef.current !== null &&
            Date.now() - hiddenAtRef.current >= THRESHOLD_MS) {
          window.location.reload();
        }
        hiddenAtRef.current = null;
      }
    };
    document.addEventListener('visibilitychange', onVisChange);
    return () => document.removeEventListener('visibilitychange', onVisChange);
  }, []);

  // Redirect to dashboard if navigating to protected tabs without permission
  useEffect(() => {
    if (!isAuthenticated) return;
  }, [isAuthenticated, role, loc.tab]);

  const setTab = useCallback((tab) => {
    setMountedTabs(prev => {
      if (prev.has(tab)) return prev;
      const next = new Set(prev);
      next.add(tab);
      return next;
    });
    setLoc({ tab, sub: '' });
    pushHash(tab, '');
    trackTabVisit(tab);
    setSidebarMobileOpen(false);   // always close mobile drawer on nav
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [trackTabVisit]);

  const setSubTab = useCallback((sub) => {
    setLoc(prev => {
      pushHash(prev.tab, sub, true);
      return { ...prev, sub };
    });
  }, []);

  // Navigate to a specific tab + sub-tab in one call (used by sidebar)
  const navigate = useCallback((newTab, newSub = '') => {
    setMountedTabs(prev => {
      if (prev.has(newTab)) return prev;
      const next = new Set(prev);
      next.add(newTab);
      return next;
    });
    setLoc({ tab: newTab, sub: newSub });
    pushHash(newTab, newSub);
    trackTabVisit(newTab);
    setSidebarMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [trackTabVisit]);

  const { tab, sub } = loc;
  const vis = (id) => (tab === id ? {} : { display: 'none' });

  return (
    <div className="app-shell" style={{ background: 'var(--paper)', color: 'var(--ink)' }}>

      {/* ── Pull-to-refresh indicator ── */}
      {pullState === 'pulling' && (
        <div style={{
          position: 'fixed', top: 52, left: '50%', transform: `translateX(-50%) scaleY(${pullPx / 64})`,
          transformOrigin: 'top center',
          zIndex: 150, background: 'var(--surface)', borderRadius: 20,
          padding: '5px 14px', fontSize: 11, color: 'var(--ink-3)',
          boxShadow: '0 2px 8px rgba(0,0,0,.18)',
          display: 'flex', alignItems: 'center', gap: 6,
          pointerEvents: 'none',
        }}>
          ↓ Pull to refresh
        </div>
      )}
      {pullState === 'refreshing' && (
        <div style={{
          position: 'fixed', top: 52, left: '50%', transform: 'translateX(-50%)',
          zIndex: 150, background: 'var(--surface)', borderRadius: 20,
          padding: '5px 14px', fontSize: 11, color: 'var(--ink-2)',
          boxShadow: '0 2px 8px rgba(0,0,0,.18)',
          display: 'flex', alignItems: 'center', gap: 6,
          pointerEvents: 'none',
        }}>
          <span style={{ display: 'inline-block', animation: 'ptr-spin .7s linear infinite' }}>↻</span>
          Refreshing…
        </div>
      )}

      <TopBar
        onMenuToggle={() => setSidebarMobileOpen(o => !o)}
        onLogoClick={() => navigate('dashboard')}
      />

      <div className="app-body">
        <Sidebar
          tab={tab}
          sub={sub}
          navigate={navigate}
          canAccessTab={canAccessTab}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(o => !o)}
          mobileOpen={sidebarMobileOpen}
          onCloseMobile={() => setSidebarMobileOpen(false)}
          onSettings={() => navigate('settings')}
        />

        <main className="app-main">

        {mountedTabs.has('dashboard') && (
          <div style={vis('dashboard')}>
            <TabErrorBoundary>
              {canAccessTab('dashboard') ? <DashboardTab/> : <LockedTab tabId="dashboard"/>}
            </TabErrorBoundary>
          </div>
        )}

        {mountedTabs.has('nifty') && (
          <div style={vis('nifty')}>
            <TabErrorBoundary>
              {canAccessTab('nifty') ? <NiftyTab/> : <LockedTab tabId="nifty"/>}
            </TabErrorBoundary>
          </div>
        )}

        {mountedTabs.has('performance') && (
          <div style={vis('performance')}>
            <TabErrorBoundary>
              {canAccessTab('performance')
                ? <PerformanceTab initSub={tab==='performance' ? sub : initSubRef.current} onSubChange={setSubTab}/>
                : <LockedTab tabId="performance"/>}
            </TabErrorBoundary>
          </div>
        )}

        {mountedTabs.has('options') && (
          <div style={vis('options')}>
            <TabErrorBoundary>
              {canAccessTab('options')
                ? <OptionsTab initSub={tab==='options' ? sub : ''} onSubChange={setSubTab}/>
                : <LockedTab tabId="options"/>}
            </TabErrorBoundary>
          </div>
        )}

        {mountedTabs.has('scanner') && (
          <div style={vis('scanner')}>
            <TabErrorBoundary>
              {canAccessTab('scanner')
                ? <ScannerTab initSub={tab==='scanner' ? sub : ''} onSubChange={setSubTab}/>
                : <LockedTab tabId="scanner"/>}
            </TabErrorBoundary>
          </div>
        )}

        {mountedTabs.has('journal') && (
          <div style={vis('journal')}>
            <TabErrorBoundary>
              {canAccessTab('journal')
                ? <JournalTab initSub={tab==='journal' ? sub : ''} onSubChange={setSubTab}/>
                : <LockedTab tabId="journal"/>}
            </TabErrorBoundary>
          </div>
        )}

        {mountedTabs.has('ai') && (
          <div style={vis('ai')}>
            <TabErrorBoundary>
              {canAccessTab('ai') ? <AISummaryTab/> : <LockedTab tabId="ai"/>}
            </TabErrorBoundary>
          </div>
        )}

        {mountedTabs.has('desk') && (
          <div style={vis('desk')}>
            <TabErrorBoundary>
              <DeskTab initSub={tab === 'desk' ? sub : ''} onSubChange={setSubTab}/>
            </TabErrorBoundary>
          </div>
        )}

        {/* Settings — all authenticated users */}
        {mountedTabs.has('settings') && (
          <div style={vis('settings')}>
            <SettingsTab initialSub={tab === 'settings' ? sub : undefined}/>
          </div>
        )}

        </main>
      </div>{/* /app-body */}

      <SidePanel/>
    </div>
  );
}

// Auth gate — decides which screen to show based on session state
function AuthGate() {
  const { loading, user } = useUser();

  if (loading)                 return <LoadingScreen/>;
  if (!user)                   return <LoginPage/>;
  if (user.role === 'pending') return <PendingPage/>;

  return (
    <SidePanelProvider>
      <PriceProvider>
        <TodayProvider>
          <AppInner/>
        </TodayProvider>
      </PriceProvider>
    </SidePanelProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <UserProvider>
        <AuthGate/>
      </UserProvider>
    </ThemeProvider>
  );
}
